import { ListType } from '@hyper-inferno/survey-elements-core';
import { ITransformationContext } from '../survey-engine/survey-engine.class';
import { cleanupSandbox, flattenResponses } from './piping-utils';

export interface CustomScriptContext {
  bundle: any;
  session: any;
  handlers: Record<string, any>;
  ctx: ITransformationContext;
}

export class CustomScriptEngine {
  /**
   * Executes arbitrary JavaScript/TypeScript string instructions safely in a sandbox environment.
   */
  public static execute(
    code: string,
    currentOptions: any[],
    context: CustomScriptContext,
  ): any[] {
    if (!code || !code.trim()) {
      return currentOptions;
    }

    // Clone working options list to preserve immutability until execution finishes
    let optionsList = [...currentOptions];

    const scope = this.createExecutionScope(
      optionsList,
      context,
      (updatedList) => {
        optionsList = updatedList;
      },
    );

    try {
      this.runInSandbox(code, scope);
    } catch (err) {
      console.error('Custom Script Execution Failed:', err);
    }

    return optionsList;
  }

  /**
   * Creates the execution context and provides helper methods for list modification inside script scope.
   */
  private static createExecutionScope(
    optionsList: any[],
    context: CustomScriptContext,
    updateOptions: (list: any[]) => void,
  ) {
    const { bundle, session, ctx } = context;

    // Retrieve options by list name
    const getListOptionsByName = (listName: string) => {
      const list = bundle.getListByListName?.(listName);
      if (!list) throw new Error(`List not found: ${listName}`);

      return ctx.listBuilding?.getOptionsByListId(list.id, ctx, false) ?? [];
    };

    // Standardized Helper API injected into the custom script scope
    const helpers = {
      // Direct access to current items
      currentOptions: optionsList,

      // Appends/Inserts options into the list
      addOption: (
        option_key: number,
        option_display: string,
        index?: number,
      ) => {
        const option = {
          option_key,
          option_html: null,
          simple_props: {},
          translations: {},
          is_open_ended: false,
          option_display,
        };

        const next = [...optionsList];
        if (typeof index === 'number' && index >= 0) {
          next.splice(index, 0, option);
        } else {
          next.push(option);
        }
        updateOptions(next);
      },

      // Removes an option by ID
      removeOptionById: (id: string | number) => {
        const next = optionsList.filter((opt) => opt.id !== id);
        updateOptions(next);
      },

      // Fetch helper for external list items
      getListOptionsByName,

      // Convenience Response map matching UniversalPiping format
      responses: cleanupSandbox(flattenResponses(session?.jsn_responses || {})),

      // Direct references
      session,
      bundle,
      ctx,
      console,
      JSON,
      Math,
    };

    return helpers;
  }

  /**
   * Runs the JavaScript string inside a Proxy-isolated sandbox
   */
  private static runInSandbox(code: string, scope: Record<string, any>): any {
    // 1. Construct dynamic function using `with` block scope
    const fn = new Function(
      'scope',
      `
      with (scope) {
        return (() => {
          "use strict";
          ${code}
        })();
      }
    `,
    );

    // 2. Proxy handler intercepts all variable lookups inside the `with` block
    const proxy = new Proxy(scope, {
      get: (target, prop) => {
        if (prop in target) return target[prop as any];
        return undefined;
      },
      has: (_target, _prop) => {
        // Force all variable reads to go through the proxy scope first
        return true;
      },
    });

    return fn(proxy);
  }
}
